import { useMemo } from "react";
import type { ProposedSlot } from "@/types/scheduling.ts";
import { parseISOToMs } from "@/lib/date-utils.ts";
import { CalendarAvailabilityPicker } from "./CalendarAvailabilityPicker.tsx";

export type { ProposedSlot } from "@/types/scheduling.ts";

type ProposedSlotSelectorProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Currently proposed slots (epoch-ms). */
  slots: ProposedSlot[];
  /** Meeting duration in minutes. */
  duration: number;
  /** Calendar starts on this date. */
  initialDate: Date;
  /** Called with the edited slot list and chosen duration when the organizer saves. */
  onSave: (slots: ProposedSlot[], durationMinutes: number) => void;
};

/**
 * Modal for editing the merged kickoff's proposed time slots. Thin wrapper
 * over `CalendarAvailabilityPicker` that maps the artifact's epoch-ms slots
 * to the picker's `{ startISO, endISO }` ranges and back. Skip-conflicts
 * doesn't apply to concrete proposed times, so it's hidden; first-edit
 * replacement is disabled (the AI-proposed list should be adjusted, not
 * wiped); the picker snaps to meeting duration.
 */
export const ProposedSlotSelector = ({
  open,
  onOpenChange,
  slots,
  duration,
  initialDate,
  onSave,
}: ProposedSlotSelectorProps) => {
  // Normalize each loaded slot to the meeting duration: AI-proposed slots
  // can carry a mis-sized length, and a save-without-edit would otherwise
  // persist it. Anchoring end to start + duration keeps the picker
  // consistent. Memoized so a new array identity doesn't churn picker effects.
  const initialRanges = useMemo(
    () =>
      slots.map((s) => ({
        startISO: new Date(s.startMsSinceEpoch).toISOString(),
        endISO: new Date(s.startMsSinceEpoch + duration * 60_000).toISOString(),
      })),
    [slots, duration],
  );

  return (
    <CalendarAvailabilityPicker
      open={open}
      onOpenChange={onOpenChange}
      initialDate={initialDate}
      duration={duration}
      title="Edit proposed times"
      description="Click or drag empty space to add a time, drag a time to move it, or click a time to remove it."
      showSkipConflictsCheckbox={false}
      replaceOnFirstEdit={false}
      snapDurationMinutes={duration}
      editableDuration
      initialRanges={initialRanges}
      onSave={(ranges, _skip, durationMinutes) => {
        // Parse with parseISOToMs (consistent with the picker stack) and drop
        // any slot that fails to parse rather than persisting a NaN time.
        const parsed = ranges
          .map((r) => ({
            startMsSinceEpoch: parseISOToMs(r.startISO),
            endMsSinceEpoch: parseISOToMs(r.endISO),
          }))
          .filter(
            (s) => Number.isFinite(s.startMsSinceEpoch) && Number.isFinite(s.endMsSinceEpoch),
          );
        onSave(parsed, durationMinutes);
      }}
    />
  );
};
